<script setup lang="ts">
import { ref, computed } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useTabComposable } from '@/components/tabs/useTabComposable';
import { formatDate } from '@/utils/dateUtils';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import ViewVersionRevertModal from '@/components/modal/ViewVersionRevertModal.vue';
import ImportFileModal from '@/components/modal/ImportFileModal.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';
import ViewGatewayDurationDataModal from '@/components/modal/ViewGatewayDurationDataModal.vue';
import ManageGatewayDestinationDuration from './ManageGatewayDestinationDuration.vue';

export type TDuration = {
  pk?: string;
  sk?: string;
  gatewayCode?: any;
  gatewayName?: string;
  destinationSVId?: any;
  destinationName?: string;
  destinationAirportCode?: string;
  cacheDuration?: any;
  additionalDuration?: any;
  expiryDate?: any;
  travelDate?: any;
  status?: any;
};

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const currentLanguage = getCurrentLocale();
const packageManagementStore = usePackageManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const destinations = computed(() => {
  return packageManagementStore.destinations;
});
const list = ref<TDuration[]>([]);
const isLoading = ref(false);
const listFilters = ref();
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);

const sheetsData = ref<any>([]);
const { styleColumnExport, renderNameFileExport } = useViewComposable();
const widthList = [10, 12, 12, 10, 12, 12, 20, 25, 15, 10, 20];
const sheetColumnStyle = styleColumnExport(widthList);
const { formatAirport } = useTabComposable();
const isLoadingImport = ref(false);
const isShowModal = ref(false);
const isLoadingPage = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);

const gateways = computed(() => {
  return formatAirport(packageManagementStore.gateways, currentLanguage);
});

const getList = () => {
  isLoading.value = true;
  packageManagementStore
    .getListGatewayDuration()
    .then((res) => {
      list.value = res?.data ?? [];
      sheetsData.value = [
        {
          name: 'Sheet 1',
          data: formatDataExport(res?.data ?? [])
        }
      ];
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getList();

FilterService.register('filterDates', (dates, filterValue) => {
  const datesFormat = formatListDateRange(dates);
  return !filterValue || datesFormat.includes(filterValue);
});

const initDestinationGroupingFilters = () => {
  listFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gatewayCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gatewayName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationSVId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationAirportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    destinationName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    cacheDuration: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    additionalDuration: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    travelDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: 'filterDates' }] },
    expiryDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    typeGatewayDuration: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};

initDestinationGroupingFilters();

const getSeverityStatus = (currentStatus: string) => {
  switch (currentStatus) {
    case STATUS_CLOSED:
      return 'danger';
    case STATUS_OPEN:
      return 'info';
    default:
      return '';
  }
};

const updateStatus = (sk: any, data: any) => {
  packageManagementStore
    .updateStatusDuration(sk, data)
    .then(() => {
      getList();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const openDialog = (header: string, data?: any) => {
  const dialogRef = dialog.open(ManageGatewayDestinationDuration, {
    data: {
      gateways: gateways,
      destinationList: destinations,
      data: data
    },
    props: {
      header: t(header),
      modal: true
    },
    emits: {
      onSave: () => {
        dialogRef.close();
        getList();
      }
    }
  });
};

const editDestinationDurations = (data: any) => {
  openDialog('management.package.manage_gateway_destination_duration', data);
};

const deleteDestinationDurations = (data: any) => {
  packageManagementStore
    .deleteGatewayDuration(data?.sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
      getList();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.delete_error'), life: 5000 });
    });
};

const newlineFromArry = (arr: number[]) => {
  return arr.reduce((prev, cur) => {
    if (!prev) {
      return prev + cur;
    }
    return prev + ', ' + cur;
  }, '');
};

const formatListDateRange = (arr: { start: string; end: string }[]) => {
  return arr.reduce((prev, cur) => {
    const formatString = `${formatDate(cur.start, 'dd/LLL/yyyy', true, currentLanguage)} - ${formatDate(cur.end, 'dd/LLL/yyyy', true, currentLanguage)}`;
    if (!prev) {
      return prev + formatString;
    }
    return prev + ', ' + formatString;
  }, '');
};

const handleDisplayDate = (listDate: any, separator = '\n') => {
  let format = '';
  if (listDate?.length) {
    format = listDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join(separator);
  }
  return format;
};

const addDestinationGroup = () => {
  openDialog('management.package.manage_gateway_destination_duration');
};

const getGatewayDurationConfiguration = (config: any) => {
  return config?.gate_duration_configuration ?? {};
};

const formatArray = (durations: string[], separate = ',') => {
  return durations ? durations.join(separate) : '';
};

const formatDataExport = (listData: any) => {
  return listData?.map((item: any) => {
    const data: any = {};
    data[t('management.package.gateway_code')] = item.gatewayCode;
    data[t('management.package.gateway_name')] = item.gatewayName;
    data[t('management.package.sv_destination_code')] = item.destinationSVId;
    data[t('management.package.destination_code')] = item.destinationAirportCode;
    data[t('management.package.destination_name')] = item.destinationName;
    data[t('management.package.cached_duration')] = formatArray(item?.cacheDuration, ',');
    data[t('management.package.additional_duration')] = formatArray(item?.additionalDuration, ',');
    data[t('hotel_contract.travel_date_range')] = handleDisplayDate(item?.travelDate, ', ');
    data[t('book_form.expiry_date')] = formatDate(item?.expiryDate, 'dd-LLL-yyyy', false, currentLanguage);
    data[t('common.status')] = item.status;
    return data;
  });
};

const handleApplyGatewayDuration = (data: any, file?: any, type?: string) => {
  const dialogRef = dialog.open(ViewGatewayDurationDataModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      gateway: data,
      type,
      sheetData: formatDataExport(data)
    },
    emits: {
      onApply: () => {
        dialogRef.close();
        if (type === 'IMPORT') {
          handleImport(file);
        } else if (type === 'REVERT') {
          handleRevert(file);
        }
      },
      onClose: () => {
        dialogRef.close();
        if (type === 'REVERT') {
          showRevertVersion('GATE_DURATION');
        }
      }
    }
  });
};

const handleImport = async (file: any) => {
  isLoadingImport.value = true;
  isShowModal.value = true;
  isRevert.value = false;
  const payload = new FormData();
  payload.append('file', file);
  payload.append('tabType', 'GATE_DURATION');
  packageManagementStore
    .importGatewayDuration(payload)
    .then(() => {
      isSuccess.value = true;
      getList();
    })
    .catch(() => {
      isSuccess.value = false;
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const handleRevert = async (fileName) => {
  isRevert.value = true;
  isLoadingImport.value = true;
  isShowModal.value = true;
  const payload = {
    fileName,
    tabType: 'GATE_DURATION'
  };
  packageManagementStore
    .revertGatewayDuration(payload)
    .then(() => {
      isSuccess.value = true;
      getList();
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const showRevertVersion = async (type: string) => {
  isLoadingPage.value = true;
  packageManagementStore
    .getVersionRevert(type)
    .then((versions) => {
      openModalRevert(versions?.data);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

const showModalImportExcel = () => {
  const dialogRef = dialog.open(ImportFileModal, {
    props: {
      header: t('common.labels.upload_gateway_duration'),
      modal: true
    },
    data: {
      url: packageManagementStore.getUrlUploadGatewayDuration
    },
    emits: {
      onApply: (data: any, file: any) => {
        handleApplyGatewayDuration(data, file, 'IMPORT');
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleCloseModal = (value: boolean) => {
  if (isRevert.value === true) {
    showRevertVersion('GATE_DURATION');
  }
  isShowModal.value = value;
};

const handlePreviewVersion = async (fileName: string, type: string) => {
  isLoadingPage.value = true;
  packageManagementStore
    .getPreviewGatewayDuration(fileName)
    .then((data) => {
      handleApplyGatewayDuration(data?.data, fileName, type);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

const openModalRevert = (versions: any) => {
  const dialogRef = dialog.open(ViewVersionRevertModal, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onPreview: (fileName: string) => {
        handlePreviewVersion(fileName, 'PREVIEW');
      },
      onRevert: (fileName: string) => {
        handlePreviewVersion(fileName, 'REVERT');
        dialogRef.close();
      },
      onExport: (fileName: string) => {
        handlePreviewVersion(fileName, 'EXPORT');
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const canModifyGroup = authorizations?.configurationPage.destination_groupings_edit;
</script>

<template>
  <div class="gateway_duration gateway-duration-management">
    <LoadingSpinner class="page_loading" v-if="isLoading || isLoadingPage" />
    <div class="card">
      <div class="flex justify-content-end" v-if="getGatewayDurationConfiguration(authorizations)?.add_duration_rule">
        <Button id="btn-add-duration" icon="pi pi-plus" :label="$t('management.package.add_duration_rule')" class="mb-2" @click="addDestinationGroup()" />
      </div>
      <DataTable
        v-model:filters="listFilters"
        :value="list"
        paginator
        showGridlines
        :rows="10"
        dataKey="id"
        filterDisplay="menu"
        :globalFilterFields="['gatewayCode', 'gatewayName', 'destinationSVId', 'status', 'destinationAirportCode', 'destinationName', 'typeGatewayDuration']"
      >
        <template #header>
          <div class="flex justify-content-between flex-col sm:flex-row">
            <div class="flex justify-content-left flex-col sm:flex-row gap-3">
              <div>
                <Button id="btn-clear-filter" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="clear-gateway-duration-btn mb-2" @click="initDestinationGroupingFilters()" />
              </div>
              <div v-if="getGatewayDurationConfiguration(authorizations)?.export_duration_rule">
                <ExportExcelFile id="btn-export-excel" class="export-gateway-duration-btn" :sheets="sheetsData" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Duration_Rule_Management')"></ExportExcelFile>
              </div>
              <div v-if="getGatewayDurationConfiguration(authorizations)?.import_duration_rule">
                <Button id="btn-import-excel" :label="$t('common.buttons.import')" class="import-gateway-duration-btn" @click="showModalImportExcel()" />
              </div>
              <div v-if="getGatewayDurationConfiguration(authorizations)?.revert_duration_rule">
                <Button id="btn-revert-excel" :label="$t('common.buttons.revert')" class="revert-gateway-duration-btn" @click="showRevertVersion('GATE_DURATION')" />
              </div>
            </div>

            <div class="justify-content-right">
              <span class="p-input-icon-left mb-2">
                <i class="pi pi-search" />
                <InputText id="input-search" v-model="listFilters.global.value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
              </span>
            </div>
          </div>
        </template>
        <template #empty> {{ $t('management.no_data_found') }} </template>
        <template #loading>
          <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
        </template>
        <TableColumn field="gatewayCode" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.gateway') }}</span>
              <span>{{ $t('management.package.code') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span class="long-text" v-tooltip="data[field]">
              {{ data[field] }}
            </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.air.search_by_gateway_code')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="gatewayName" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.gateway_name') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span class="long-text" v-tooltip="data[field]">
              {{ data[field] }}
            </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.air.search_by_gateway_name')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="destinationSVId" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.sv_dest_code') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <div class="flex flex-col">
              <span>{{ data[field] }} </span>
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination_id')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="destinationAirportCode" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.destination') }}</span>
              <span>{{ $t('management.air.airport_code') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span>{{ data[field] }} </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination_airport_code')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="destinationName" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.destination_name') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span>{{ data[field] }} </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.air.search_by_destination_name')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="cacheDuration" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.cached_duration') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span>{{ newlineFromArry(data[field]) }} </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_cached_duration')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="additionalDuration" :showAddButton="false" :showFilterOperator="false">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('management.package.additional_duration') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span>{{ newlineFromArry(data[field]) }} </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_additional_durations')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="travelDate" :showAddButton="false" :showFilterOperator="false" :showFilterMatchModes="false" dataType="date">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('hotel_contract.travel_date_range') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <div>{{ formatListDateRange(data[field]) }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_travel_date')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="expiryDate" :showAddButton="false" :showFilterOperator="false" dataType="date">
          <template #header>
            <div class="flex flex-col">
              <span>{{ $t('book_form.expiry_date') }}</span>
            </div>
          </template>
          <template #body="{ data, field }">
            <span>{{ formatDate(data[field], 'dd-LLL-yyyy', true, currentLanguage) }} </span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_expiry_date')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="typeGatewayDuration" :header="$t('management.package.type')">
          <template #body="{ data, field }">
            <div>{{ data[field] }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.package.search_by_type')" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn field="status" :header="$t('common.status')" :showAddButton="false" :showFilterOperator="false">
          <template #body="{ data }">
            <ToggleSwitch :disabled="!canModifyGroup || !getGatewayDurationConfiguration(authorizations)?.status_duration_rule" v-model="data.status" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="updateStatus(data.sk, data.status)" />
          </template>
          <template #filter="{ filterModel }">
            <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverityStatus(slotProps.option)" />
              </template>
            </Select>
          </template>
          <template #filterclear="{ filterCallback }">
            <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
          </template>
        </TableColumn>
        <TableColumn v-if="canModifyGroup" :header="$t('common.action')">
          <template #body="{ data }">
            <div class="flex">
              <Button v-if="getGatewayDurationConfiguration(authorizations)?.edit_duration_rule" icon="pi pi-pencil" class="p-button-text" @click="editDestinationDurations(data)" />
              <Button v-if="getGatewayDurationConfiguration(authorizations)?.delete_duration_rule" icon="pi pi-trash" class="p-button-text" @click="deleteDestinationDurations(data)" />
            </div>
          </template>
        </TableColumn>
      </DataTable>
      <Dialog v-model:visible="isShowModal" :header="$t('common.labels.uploading_gateway_duration')" modal :closable="!isLoadingImport">
        <ProgressModal :isProgress="isLoadingImport" @handleClose="handleCloseModal" :isSuccess="isSuccess" :isRevert="isRevert" />
      </Dialog>
    </div>
  </div>
</template>
